import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getErrorMessage } from '../utils/api'

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const { login, loading, error, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Where to go after a successful login. ProtectedRoute stashes the original
  // destination in location.state.from (e.g. a /tickets/:id deep link from a
  // team email); fall back to the home route when there's nothing to return to.
  const from =
    (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)
      ?.from
  const redirectTo = from?.pathname
    ? `${from.pathname}${from.search || ''}${from.hash || ''}`
    : '/'

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true })
  }, [isAuthenticated, navigate, redirectTo])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)

    if (!email || !password) {
      setLocalError('Please fill in all fields')
      return
    }

    try {
      await login(email, password)
      // navigate is triggered by the useEffect watching isAuthenticated
    } catch (err) {
      setLocalError(getErrorMessage(err, 'Login failed'))
    }
  }

  const displayError = localError || error

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 via-white to-brand-100 dark:from-[#020617] dark:via-[#0f172a] dark:to-[#1e293b] p-4 transition-colors duration-300">
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-brand-400/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-600/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="relative w-full max-w-md">
        {/* Card */}
        <div className="bg-surface-elevated rounded-2xl shadow-float border border-border p-8 animate-slide-up">
          {/* Brand header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-600 text-white font-bold text-xl mb-4 shadow-glow">
              W
            </div>
            <h1 className="text-2xl font-bold text-txt-primary">
              Welcome back
            </h1>
            <p className="text-sm text-txt-secondary mt-1">
              Sign in to your WeQuote agent dashboard
            </p>
          </div>

          {/* Error */}
          {displayError && (
            <div className="flex items-center gap-2 px-4 py-3 mb-6 rounded-lg bg-error-light border border-error/20 text-error text-sm animate-fade-in">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              {displayError}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-txt-primary mb-1.5">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="agent@wequote.com"
                disabled={loading}
                autoComplete="email"
                className="w-full px-3.5 py-2.5 rounded-lg bg-surface border border-border text-txt-primary placeholder-txt-tertiary text-sm outline-none transition-all duration-150 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:opacity-50"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-txt-primary mb-1.5">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={loading}
                autoComplete="current-password"
                className="w-full px-3.5 py-2.5 rounded-lg bg-surface border border-border text-txt-primary placeholder-txt-tertiary text-sm outline-none transition-all duration-150 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:opacity-50"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 rounded-lg bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white font-semibold text-sm transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-surface"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4 animate-spin-slow" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Dev hint — only show in development */}
          {import.meta.env.DEV && (
            <div className="mt-6 pt-5 border-t border-border">
              <p className="text-xs text-txt-tertiary text-center">
                Dev mode: use any email (e.g. sarah@wequote.com) with password &quot;password&quot;
              </p>
            </div>
          )}
        </div>

        {/* Branding footer */}
        <p className="text-center text-xs text-txt-tertiary mt-6">
          WeQuote Support Platform &middot; Powered by AI
        </p>
      </div>
    </div>
  )
}
